import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, MapPin, Image as ImageIcon, X, Save, Loader2, ArrowUp, ArrowDown, Lock, Unlock, Map } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TreasureEditor() {
  const [meta, setMeta] = useState({ title: '', subtitle: '', intro: '', footer: '', is_live: true });
  const [clues, setClues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        axios.get(`${API}/api/admin/treasure/meta`, { withCredentials: true }),
        axios.get(`${API}/api/admin/treasure/clues`, { withCredentials: true }),
      ]);
      setMeta(m.data);
      setClues(c.data);
    } catch (e) { /* auth interceptor */ }
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  const saveMeta = async () => {
    setSaving(true);
    await axios.put(`${API}/api/admin/treasure/meta`, meta, { withCredentials: true });
    setSaving(false);
    setNotice('Hunt settings saved.');
    setTimeout(() => setNotice(''), 2500);
  };

  const addClue = async () => {
    const order = clues.length + 1;
    const { data } = await axios.post(`${API}/api/admin/treasure/clues`, {
      order, title: `Clue ${order}`, description: '', latitude: null, longitude: null,
      image_base64: null, hint: '', unlocked: true,
    }, { withCredentials: true });
    setClues(list => [...list, data]);
  };

  const updateClue = async (id, patch) => {
    const cur = clues.find(c => c.id === id);
    const next = { ...cur, ...patch };
    setClues(list => list.map(c => c.id === id ? next : c));
    await axios.put(`${API}/api/admin/treasure/clues/${id}`, next, { withCredentials: true });
  };

  const deleteClue = async (id) => {
    await axios.delete(`${API}/api/admin/treasure/clues/${id}`, { withCredentials: true });
    setClues(list => list.filter(c => c.id !== id));
  };

  const moveClue = async (id, dir) => {
    const idx = clues.findIndex(c => c.id === id);
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= clues.length) return;
    const a = clues[idx], b = clues[swap];
    const newClues = [...clues];
    newClues[idx] = { ...a, order: b.order };
    newClues[swap] = { ...b, order: a.order };
    newClues.sort((x, y) => x.order - y.order);
    setClues(newClues);
    await Promise.all([
      axios.put(`${API}/api/admin/treasure/clues/${a.id}`, { ...a, order: b.order }, { withCredentials: true }),
      axios.put(`${API}/api/admin/treasure/clues/${b.id}`, { ...b, order: a.order }, { withCredentials: true }),
    ]);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-wide uppercase text-white mb-1 flex items-center gap-2">
            <Map size={20} className="text-brand" /> Treasure <span className="text-brand">Hunt</span>
          </h2>
          <p className="font-sans text-xs text-neutral-500">Manage the geocache hunt shown when users click the lightbulb on the website.</p>
        </div>
        <a href="/treasure" target="_blank" rel="noopener noreferrer" className="text-[11px] font-heading tracking-widest uppercase text-brand hover:text-brand-hover">
          Open public page →
        </a>
      </div>

      {notice && (
        <div className="bg-brand/10 border border-brand/30 rounded-xl px-4 py-3 text-brand font-sans text-sm">{notice}</div>
      )}

      {/* Meta */}
      <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Hunt Settings</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Live</span>
            <input type="checkbox" checked={meta.is_live} onChange={e => setMeta(m => ({ ...m, is_live: e.target.checked }))} className="w-4 h-4 accent-[#e6373f]" />
          </label>
        </div>
        <Field label="Title" value={meta.title} onChange={v => setMeta(m => ({ ...m, title: v }))} />
        <Field label="Subtitle" value={meta.subtitle} onChange={v => setMeta(m => ({ ...m, subtitle: v }))} />
        <Field label="Intro Paragraph" value={meta.intro} onChange={v => setMeta(m => ({ ...m, intro: v }))} multi />
        <Field label="Footer Text" value={meta.footer} onChange={v => setMeta(m => ({ ...m, footer: v }))} />

        <div className="pt-3 border-t border-neutral-800 space-y-1">
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 block">
            Hint Contact URL
          </label>
          <input
            type="text"
            value={meta.hint_contact || ''}
            onChange={e => setMeta(m => ({ ...m, hint_contact: e.target.value }))}
            placeholder="https://m.me/okiescape  · or ·  sms:+81909898890319  · or ·  mailto:info@okiescape.com"
            className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2.5 text-white font-sans text-sm focus:border-brand focus:outline-none"
            data-testid="meta-hint-contact"
          />
          <p className="font-sans text-[11px] text-neutral-500 leading-snug">
            When a user clicks "Stuck? Message us for a hint" on any clue, this opens a prefilled message to you — Messenger, SMS, WhatsApp, or email all supported.
          </p>
          <ul className="font-sans text-[10px] text-neutral-600 space-y-0.5 pl-3 list-disc">
            <li><b className="text-neutral-400">Facebook Messenger:</b> <code>https://m.me/okiescape</code></li>
            <li><b className="text-neutral-400">WhatsApp:</b> <code>https://wa.me/819098890319</code></li>
            <li><b className="text-neutral-400">SMS:</b> <code>sms:+819098890319</code></li>
            <li><b className="text-neutral-400">Email:</b> <code>mailto:info@okiescape.com</code></li>
            <li><b className="text-neutral-400">Instagram profile:</b> <code>https://instagram.com/okiescape</code></li>
          </ul>
        </div>

        <button onClick={saveMeta} disabled={saving} className="inline-flex items-center gap-2 bg-brand text-white font-heading text-[11px] tracking-widest uppercase px-4 py-2 rounded-lg hover:bg-brand-hover disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
        </button>
      </section>

      {/* Clues */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Clues ({clues.length})</h3>
          <button onClick={addClue} className="inline-flex items-center gap-1.5 bg-brand text-white font-heading text-[11px] tracking-widest uppercase px-3 py-2 rounded-lg hover:bg-brand-hover"><Plus size={12} /> Add Clue</button>
        </div>

        {loading && <p className="text-neutral-500 text-xs font-sans">Loading...</p>}

        {!loading && clues.length === 0 && (
          <div className="text-center py-12 border border-dashed border-neutral-800 rounded-xl">
            <MapPin size={36} className="text-neutral-700 mx-auto mb-2" />
            <p className="font-sans text-sm text-neutral-500">No clues yet. Add the first one above.</p>
          </div>
        )}

        {clues.map((c, i) => (
          <ClueRow
            key={c.id}
            index={i}
            total={clues.length}
            clue={c}
            onChange={(patch) => updateClue(c.id, patch)}
            onDelete={() => deleteClue(c.id)}
            onMove={(dir) => moveClue(c.id, dir)}
          />
        ))}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, multi }) {
  return (
    <div>
      <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">{label}</label>
      {multi ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2.5 text-white font-sans text-sm focus:border-brand focus:outline-none resize-y" />
      ) : (
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2.5 text-white font-sans text-sm focus:border-brand focus:outline-none" />
      )}
    </div>
  );
}

function ClueRow({ index, total, clue, onChange, onDelete, onMove }) {
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = reader.result.toString().split(',')[1];
      onChange({ image_base64: b64 });
    };
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  return (
    <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand/15 border border-brand/30 flex items-center justify-center font-heading text-sm text-brand">
            {String(index + 1).padStart(2, '0')}
          </div>
          <span className="font-heading text-[10px] tracking-widest uppercase text-neutral-500">CLUE</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onChange({ unlocked: !clue.unlocked })} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-heading tracking-widest uppercase ${clue.unlocked ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-neutral-800 text-neutral-400 border border-neutral-700'}`}>
            {clue.unlocked ? <><Unlock size={10} /> Unlocked</> : <><Lock size={10} /> Sealed</>}
          </button>
          <button onClick={() => onMove('up')} disabled={index === 0} className="p-1.5 text-neutral-500 hover:text-white disabled:opacity-30"><ArrowUp size={14} /></button>
          <button onClick={() => onMove('down')} disabled={index === total - 1} className="p-1.5 text-neutral-500 hover:text-white disabled:opacity-30"><ArrowDown size={14} /></button>
          {confirming ? (
            <button onClick={onDelete} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-brand text-white font-heading text-[10px] tracking-widest uppercase"><Trash2 size={10} /> Confirm</button>
          ) : (
            <button onClick={() => { setConfirming(true); setTimeout(() => setConfirming(false), 3000); }} className="p-1.5 text-neutral-500 hover:text-brand"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      <Field label="Title" value={clue.title} onChange={v => onChange({ title: v })} />
      <Field label="Description / Story" value={clue.description} onChange={v => onChange({ description: v })} multi />
      <Field label="Hint (shown in italics on the clue page)" value={clue.hint} onChange={v => onChange({ hint: v })} />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Latitude</label>
          <input type="number" step="any" value={clue.latitude ?? ''} onChange={e => onChange({ latitude: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="26.3344" className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none" />
        </div>
        <div>
          <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-1 block">Longitude</label>
          <input type="number" step="any" value={clue.longitude ?? ''} onChange={e => onChange({ longitude: e.target.value === '' ? null : parseFloat(e.target.value) })} placeholder="127.8056" className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none" />
        </div>
      </div>

      <div>
        <label className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 mb-2 block">Picture</label>
        <div className="flex items-center gap-3">
          {clue.image_base64 ? (
            <div className="relative w-28 h-28 rounded-lg overflow-hidden border border-neutral-800">
              <img src={`data:image/jpeg;base64,${clue.image_base64}`} alt="clue" className="w-full h-full object-cover" />
              <button onClick={() => onChange({ image_base64: null })} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 hover:bg-brand flex items-center justify-center">
                <X size={12} className="text-white" />
              </button>
            </div>
          ) : (
            <div className="w-28 h-28 rounded-lg border-2 border-dashed border-neutral-800 flex items-center justify-center">
              <ImageIcon size={24} className="text-neutral-700" />
            </div>
          )}
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-700 text-white font-heading text-[11px] tracking-widest uppercase px-3 py-2 rounded-lg">
              {clue.image_base64 ? 'Replace Image' : 'Upload Image'}
            </button>
            <p className="text-[10px] text-neutral-600 mt-1 font-sans">JPG/PNG recommended.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
